<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  agents: { type: Array, default: () => [] },
  inboxes: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  pipelines: { type: Array, default: () => [] },
  appliedFilters: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['apply', 'clear', 'close']);

const af = props.appliedFilters || {};
const selectedAgent = ref(af.agentId || '');
const selectedInbox = ref(af.inboxId || '');
const selectedTag = ref(af.label || '');
const selectedPipeline = ref(af.pipelineId || '');
const selectedStage = ref(af.stageId || '');
const selectedWindow = ref(af.windowStatus || '');
const selectedDate = ref(af.lastMessageDate || '');
const selectedOrder = ref(af.order || 'latest');

const openDropdown = ref('');

const stageOptions = computed(() => {
  if (!selectedPipeline.value) return [];
  const p = props.pipelines.find(x => String(x.id) === String(selectedPipeline.value));
  return p?.stages || [];
});

function toggleDrop(name) {
  openDropdown.value = openDropdown.value === name ? '' : name;
}

function selectOption(field, value) {
  if (field === 'agent') selectedAgent.value = value;
  else if (field === 'inbox') selectedInbox.value = value;
  else if (field === 'tag') selectedTag.value = value;
  else if (field === 'pipeline') {
    selectedPipeline.value = value;
    selectedStage.value = '';
  }
  else if (field === 'stage') selectedStage.value = value;
  else if (field === 'window') selectedWindow.value = value;
  else if (field === 'order') selectedOrder.value = value;
  openDropdown.value = '';
}

function getAgentName(id) {
  if (!id) return 'Todos';
  const a = props.agents.find(x => String(x.id) === String(id));
  return a?.name || 'Todos';
}
function getInboxName(id) {
  if (!id) return 'Todos';
  const i = props.inboxes.find(x => String(x.id) === String(id));
  return i?.name || 'Todos';
}
function getTagName(val) {
  return val || 'Todos';
}
function getPipelineName(id) {
  if (!id) return 'Nenhum selecionado';
  const p = props.pipelines.find(x => String(x.id) === String(id));
  return p?.name || 'Nenhum selecionado';
}
function getStageName(id) {
  if (!id) return 'Todas as etapas';
  const s = stageOptions.value.find(x => String(x.id) === String(id));
  return s?.name || 'Todas as etapas';
}
function getWindowName(val) {
  if (val === 'inside') return 'Dentro da janela';
  if (val === 'outside') return 'Fora da janela';
  return 'Todos';
}
function getOrderName(val) {
  return val === 'oldest' ? 'Mais antigas' : 'Mais recentes';
}

function formatDate(d) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function applyFilters() {
  openDropdown.value = '';
  emit('apply', {
    agentId: selectedAgent.value || null,
    inboxId: selectedInbox.value || null,
    label: selectedTag.value || null,
    pipelineId: selectedPipeline.value || null,
    stageId: selectedStage.value || null,
    windowStatus: selectedWindow.value || null,
    lastMessageDate: selectedDate.value || null,
    order: selectedOrder.value,
  });
}

function clearFilters() {
  selectedAgent.value = '';
  selectedInbox.value = '';
  selectedTag.value = '';
  selectedPipeline.value = '';
  selectedStage.value = '';
  selectedWindow.value = '';
  selectedDate.value = '';
  selectedOrder.value = 'latest';
  openDropdown.value = '';
  emit('clear');
}

function onOutsideClick(e) {
  if (openDropdown.value && !e.target.closest('.fp-drop-wrap')) {
    openDropdown.value = '';
  }
}

onMounted(() => document.addEventListener('mousedown', onOutsideClick));
onUnmounted(() => document.removeEventListener('mousedown', onOutsideClick));
</script>

<template>
  <div class="fp-panel">
    <div class="fp-header">
      <span class="fp-title">Filtros</span>
      <button class="fp-close" @click="emit('close')" title="Fechar filtros">
        ✕
      </button>
    </div>

    <div class="fp-body">
      <!-- Atendentes -->
      <div class="fp-group">
        <span class="fp-label">Atendentes</span>
        <div class="fp-drop-wrap">
          <button class="fp-trigger" @click="toggleDrop('agent')">
            <span class="fp-trigger-text">{{ getAgentName(selectedAgent) }}</span>
            <span class="i-lucide-chevron-down fp-trigger-icon" />
          </button>
          <div v-if="openDropdown === 'agent'" class="fp-menu">
            <button class="fp-opt" :class="{ on: !selectedAgent }" @click="selectOption('agent', '')">Todos</button>
            <button v-for="a in agents" :key="a.id" class="fp-opt" :class="{ on: String(selectedAgent) === String(a.id) }" @click="selectOption('agent', a.id)">{{ a.name }}</button>
          </div>
        </div>
      </div>

      <!-- Caixas de entrada -->
      <div class="fp-group">
        <span class="fp-label">Caixas de entrada</span>
        <div class="fp-drop-wrap">
          <button class="fp-trigger" @click="toggleDrop('inbox')">
            <span class="fp-trigger-text">{{ getInboxName(selectedInbox) }}</span>
            <span class="i-lucide-chevron-down fp-trigger-icon" />
          </button>
          <div v-if="openDropdown === 'inbox'" class="fp-menu">
            <button class="fp-opt" :class="{ on: !selectedInbox }" @click="selectOption('inbox', '')">Todos</button>
            <button v-for="i in inboxes" :key="i.id" class="fp-opt" :class="{ on: String(selectedInbox) === String(i.id) }" @click="selectOption('inbox', i.id)">{{ i.name }}</button>
          </div>
        </div>
      </div>

      <!-- Tags -->
      <div class="fp-group">
        <span class="fp-label">Tags</span>
        <div class="fp-drop-wrap">
          <button class="fp-trigger" @click="toggleDrop('tag')">
            <span class="fp-trigger-text">{{ getTagName(selectedTag) }}</span>
            <span class="i-lucide-chevron-down fp-trigger-icon" />
          </button>
          <div v-if="openDropdown === 'tag'" class="fp-menu">
            <button class="fp-opt" :class="{ on: !selectedTag }" @click="selectOption('tag', '')">Todos</button>
            <button v-for="l in labels" :key="l.id" class="fp-opt" :class="{ on: selectedTag === l.title }" @click="selectOption('tag', l.title)">{{ l.title }}</button>
          </div>
        </div>
      </div>

      <!-- Negócio na etapa -->
      <div class="fp-group">
        <span class="fp-label">Negócio na etapa</span>
        <div class="fp-drop-wrap">
          <button class="fp-trigger" @click="toggleDrop('pipeline')">
            <span class="fp-trigger-text">{{ getPipelineName(selectedPipeline) }}</span>
            <span class="i-lucide-chevron-down fp-trigger-icon" />
          </button>
          <div v-if="openDropdown === 'pipeline'" class="fp-menu">
            <button class="fp-opt" :class="{ on: !selectedPipeline }" @click="selectOption('pipeline', '')">Nenhum selecionado</button>
            <button v-for="p in pipelines" :key="p.id" class="fp-opt" :class="{ on: String(selectedPipeline) === String(p.id) }" @click="selectOption('pipeline', p.id)">{{ p.name }}</button>
          </div>
        </div>
        <div v-if="stageOptions.length" class="fp-drop-wrap" style="margin-top:4px">
          <button class="fp-trigger" @click="toggleDrop('stage')">
            <span class="fp-trigger-text">{{ getStageName(selectedStage) }}</span>
            <span class="i-lucide-chevron-down fp-trigger-icon" />
          </button>
          <div v-if="openDropdown === 'stage'" class="fp-menu">
            <button class="fp-opt" :class="{ on: !selectedStage }" @click="selectOption('stage', '')">Todas as etapas</button>
            <button v-for="s in stageOptions" :key="s.id" class="fp-opt" :class="{ on: String(selectedStage) === String(s.id) }" @click="selectOption('stage', s.id)">{{ s.name }}</button>
          </div>
        </div>
      </div>

      <!-- Janela em atendimento -->
      <div class="fp-group">
        <span class="fp-label">Janela em atendimento</span>
        <div class="fp-drop-wrap">
          <button class="fp-trigger" @click="toggleDrop('window')">
            <span class="fp-trigger-text">{{ getWindowName(selectedWindow) }}</span>
            <span class="i-lucide-chevron-down fp-trigger-icon" />
          </button>
          <div v-if="openDropdown === 'window'" class="fp-menu">
            <button class="fp-opt" :class="{ on: !selectedWindow }" @click="selectOption('window', '')">Todos</button>
            <button class="fp-opt" :class="{ on: selectedWindow === 'inside' }" @click="selectOption('window', 'inside')">Dentro da janela</button>
            <button class="fp-opt" :class="{ on: selectedWindow === 'outside' }" @click="selectOption('window', 'outside')">Fora da janela</button>
          </div>
        </div>
      </div>

      <!-- Data da última mensagem -->
      <div class="fp-group">
        <span class="fp-label">Data da última mensagem</span>
        <div class="fp-drop-wrap">
          <button class="fp-trigger" @click="toggleDrop('date')">
            <span class="fp-trigger-text">{{ selectedDate ? formatDate(selectedDate) : 'Nenhuma data selecionada' }}</span>
            <span class="i-lucide-calendar fp-trigger-icon" />
          </button>
          <div v-if="openDropdown === 'date'" class="fp-menu fp-menu--date" @mousedown.stop>
            <input v-model="selectedDate" type="date" class="fp-date-input" @change="openDropdown = ''" />
            <button v-if="selectedDate" class="fp-opt" @click="selectedDate = ''; openDropdown = ''">Limpar data</button>
          </div>
        </div>
      </div>

      <!-- Ordem -->
      <div class="fp-group">
        <span class="fp-label">Ordem</span>
        <div class="fp-drop-wrap">
          <button class="fp-trigger" @click="toggleDrop('order')">
            <span class="fp-trigger-text">{{ getOrderName(selectedOrder) }}</span>
            <span class="i-lucide-chevron-down fp-trigger-icon" />
          </button>
          <div v-if="openDropdown === 'order'" class="fp-menu">
            <button class="fp-opt" :class="{ on: selectedOrder === 'latest' }" @click="selectOption('order', 'latest')">Mais recentes</button>
            <button class="fp-opt" :class="{ on: selectedOrder === 'oldest' }" @click="selectOption('order', 'oldest')">Mais antigas</button>
          </div>
        </div>
      </div>
    </div>

    <div class="fp-footer">
      <button class="fp-btn fp-btn--clear" @click="clearFilters">Limpar filtros</button>
      <button class="fp-btn fp-btn--apply" @click="applyFilters">Aplicar filtros</button>
    </div>
  </div>
</template>

<style scoped>
.fp-panel {
  display: flex;
  flex-direction: column;
  width: 240px;
  height: 100%;
  background: rgb(var(--solid-1));
  border-left: 1px solid rgb(var(--slate-3));
  flex-shrink: 0;
}
.fp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 8px;
}
.fp-title {
  font-size: 14px;
  font-weight: 700;
  color: rgb(var(--slate-12));
}
.fp-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px; height: 26px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: rgb(var(--slate-10));
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  transition: background 0.12s, color 0.12s;
}
.fp-close:hover {
  color: rgb(var(--slate-12));
  background: rgb(var(--slate-3));
}

.fp-body {
  flex: 1;
  overflow-y: auto;
  padding: 6px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.fp-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.fp-label {
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--slate-11));
}

/* Custom dropdown */
.fp-drop-wrap {
  position: relative;
}
.fp-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 30px;
  padding: 0 8px;
  border: none !important;
  border-bottom: 1px solid rgb(var(--slate-3)) !important;
  background: transparent !important;
  color: rgb(var(--slate-12));
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.1s;
  border-radius: 0 !important;
}
.fp-trigger:hover {
  border-bottom-color: rgb(var(--slate-10));
}
.fp-trigger-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fp-trigger-icon {
  font-size: 12px;
  color: rgb(var(--slate-10));
  flex-shrink: 0;
  margin-left: 4px;
}

.fp-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 180px;
  overflow-y: auto;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--slate-3));
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 999;
  padding: 2px 0;
}
.fp-opt {
  display: block;
  width: 100%;
  padding: 6px 10px;
  border: none;
  background: transparent;
  color: rgb(var(--slate-11));
  font-size: 12.5px;
  text-align: left;
  cursor: pointer;
  transition: background 0.08s;
}
.fp-opt:hover {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
}
.fp-opt.on {
  color: #6366f1;
  font-weight: 600;
}

/* Date input inside dropdown */
.fp-menu--date {
  padding: 6px;
}
.fp-date-input {
  width: 100% !important;
  height: 32px !important;
  padding: 0 8px !important;
  border: 1px solid rgb(var(--slate-3)) !important;
  border-radius: 0 !important;
  background: rgb(var(--solid-1)) !important;
  color: rgb(var(--slate-12)) !important;
  font-size: 13px !important;
  outline: none !important;
  box-shadow: none !important;
  appearance: auto !important;
}
.fp-date-input:focus {
  border-color: #6366f1 !important;
}

/* Footer */
.fp-footer {
  display: flex;
  gap: 6px;
  padding: 10px 14px;
  border-top: 1px solid rgb(var(--slate-3));
}
.fp-btn {
  flex: 1;
  height: 30px;
  border: none !important;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s;
  border-radius: 0 !important;
}
.fp-btn--clear {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
}
.fp-btn--clear:hover { background: rgb(var(--slate-6)); }
.fp-btn--apply {
  background: #6366f1;
  color: #fff;
}
.fp-btn--apply:hover { background: #4f46e5; }
</style>
